import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, UserPlus, Check, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function CustomerPicker({ customers, value, onChange, t }) {
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
    const [dups, setDups] = useState(null);
    const [saving, setSaving] = useState(false);

    const selected = customers.find((c) => c.id === value);
    const results = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return customers.slice(0, 8);
        return customers.filter((c) => `${c.first_name} ${c.last_name} ${c.phone} ${c.email}`.toLowerCase().includes(s)).slice(0, 8);
    }, [q, customers]);

    const pick = (c) => { onChange(c.id); setOpen(false); setQ(""); setCreating(false); setDups(null); };
    const startCreate = () => { setCreating(true); setDups(null); setForm({ first_name: q.match(/^[^\d@]+$/) ? q.trim() : "", last_name: "", phone: q.match(/^[\d\s+]+$/) ? q.trim() : "", email: q.includes("@") ? q.trim() : "" }); };

    const create = async (force = false) => {
        if (!form.first_name.trim()) { toast.error(t("sched.firstNameRequired")); return; }
        setSaving(true);
        try {
            if (!force && (form.email || form.phone)) {
                const { data } = await api.get("/customers/duplicates", { params: { email: form.email, phone: form.phone } });
                if (data.length) { setDups(data); setSaving(false); return; }
            }
            const { data } = await api.post("/customers", { ...form, email: form.email.trim(), phone: form.phone.trim() });
            qc.setQueryData(["customers"], (old) => [...(old || []), data]);
            toast.success(t("sched.customerCreated"));
            pick(data);
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <div data-testid="customer-picker">
            <Label>{t("calendar.customer")}</Label>
            {selected && !open ? (
                <div className="mt-1.5 flex items-center justify-between rounded-lg border border-border/70 bg-secondary/30 px-3 py-2 text-sm" data-testid="customer-selected">
                    <span className="flex items-center gap-2 font-medium"><Check className="h-4 w-4 text-accent" />{selected.first_name} {selected.last_name}<span className="text-xs text-muted-foreground">{selected.phone || selected.email}</span></span>
                    <button type="button" onClick={() => setOpen(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="customer-change">{t("common.edit")}</button>
                </div>
            ) : (
                <div className="relative mt-1.5">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input autoFocus={open} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); setCreating(false); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
                        placeholder={t("sched.searchCustomer")} className="pl-9" data-testid="customer-search" />
                    {open && !creating && (
                        <div onMouseDown={(e) => e.preventDefault()} className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-border/70 bg-card shadow-lg" data-testid="customer-results">
                            {results.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">{t("sched.noCustomerFound")}</p>}
                            {results.map((c) => (
                                <button key={c.id} type="button" onClick={() => pick(c)} data-testid={`customer-option-${c.id}`}
                                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-secondary/50">
                                    <span>{c.first_name} {c.last_name}</span><span className="text-xs text-muted-foreground">{c.phone || c.email}</span>
                                </button>
                            ))}
                            <button type="button" onClick={startCreate} data-testid="customer-create-btn"
                                className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-sm font-medium text-accent transition-colors duration-150 hover:bg-secondary/50">
                                <UserPlus className="h-4 w-4" />+ {t("sched.newCustomer")}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {creating && (
                <div className="mt-2 space-y-3 rounded-xl border border-accent/40 bg-accent/5 p-3" data-testid="quick-customer-form">
                    <div className="flex items-center justify-between"><p className="text-sm font-semibold">{t("sched.newCustomer")}</p><button type="button" onClick={() => setCreating(false)} data-testid="quick-customer-close"><X className="h-4 w-4 text-muted-foreground" /></button></div>
                    <div className="grid grid-cols-2 gap-2">
                        <Input placeholder={`${t("customers.firstName")} *`} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="qc-first-name" />
                        <Input placeholder={t("customers.lastName")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="qc-last-name" />
                        <Input placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="qc-phone" />
                        <Input placeholder={t("common.email")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="qc-email" />
                    </div>
                    {!form.email && !form.phone && <p className="text-xs text-muted-foreground">{t("sched.contactRequired")}</p>}
                    {dups ? (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="duplicate-warning">
                            <p className="flex items-center gap-1.5 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />{t("sched.possibleDuplicate")}</p>
                            {dups.map((d) => (
                                <div key={d.id} className="mt-2 flex items-center justify-between gap-2">
                                    <span>{d.first_name} {d.last_name}<span className="ml-2 text-xs text-muted-foreground">{d.phone} {d.email}</span></span>
                                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => pick(d)} data-testid={`use-existing-${d.id}`}>{t("sched.useExisting")}</Button>
                                </div>
                            ))}
                            <Button size="sm" variant="ghost" className="mt-2 h-7 rounded-full text-xs" onClick={() => create(true)} disabled={saving} data-testid="create-anyway">{t("sched.createAnyway")}</Button>
                        </div>
                    ) : (
                        <Button size="sm" onClick={() => create(false)} disabled={saving} className="rounded-full" data-testid="qc-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
                    )}
                </div>
            )}
        </div>
    );
}
